import { AddAssetSection } from '@client/components/ltc-financial-planning/AddAssetSection';
import { FundingAllocationCard } from '@client/components/ltc-financial-planning/FundingAllocationCard';
import { formatCurrency } from '@shared';

import { useFundingStep } from '../hooks/useFundingStep';
import { StepContentProps } from '../types';

export function FundingStep(props: StepContentProps) {
  const {
    yearsTillLtc,
    contributionMonthCount,
    paymentOption,
    policyPremium,
    fundableAssets,
    priorityAssets,
    totalAllocation,
    remainingToFund,
    isFunded,
    fundingProgress,
    paymentDescription,
    handleToggleFundingSource,
    handleFundingModeChange,
    handleAllocationChange,
    getAssetCardData,
  } = useFundingStep();

  return (
    <div className="py-8">
      {/* Header */}
      <FundingStepHeader
        paymentOption={paymentOption}
        paymentDescription={paymentDescription}
      />

      {/* Policy Cost Summary */}
      <PolicyCostSummary
        paymentOption={paymentOption}
        paymentDescription={paymentDescription}
        policyPremium={policyPremium}
        totalAllocation={totalAllocation}
        remainingToFund={remainingToFund}
        fundingProgress={fundingProgress}
        isFunded={isFunded}
      />

      {/* Asset Cards */}
      <div className="mx-auto max-w-3xl space-y-4">
        {fundableAssets.length > 0 ? (
          fundableAssets.map(assetType => {
            const { detail, allocation, mode, isSelected } = getAssetCardData(assetType);

            return (
              <FundingAllocationCard
                key={assetType}
                assetType={assetType}
                accountDetail={detail}
                allocation={allocation}
                isPriority={false}
                isSelected={isSelected}
                yearsToLtc={yearsTillLtc}
                contributionMonthCount={contributionMonthCount}
                paymentOption={paymentOption}
                fundingMode={mode}
                onToggleSelection={() => handleToggleFundingSource(assetType)}
                onAllocationChange={amount => handleAllocationChange(assetType, amount)}
                onFundingModeChange={newMode => handleFundingModeChange(assetType, newMode)}
              />
            );
          })
        ) : (
          <EmptyFundableAssetsMessage />
        )}

        <AddAssetSection />
      </div>

      {/* Protected Assets Notice */}
      {priorityAssets.length > 0 && <ProtectedAssetsNotice count={priorityAssets.length} />}
    </div>
  );
}

interface FundingStepHeaderProps {
  paymentOption: LtcFinancialPlanningPaymentOption;
  paymentDescription: string;
}

function FundingStepHeader({ paymentOption, paymentDescription }: FundingStepHeaderProps) {
  return (
    <div className="text-center">
      <h1 className="text-2xl font-bold text-gray-900 md:text-3xl">
        How would you like to fund this policy?
      </h1>
      <p className="mx-auto mt-3 max-w-2xl text-base text-gray-700">
        {paymentOption === 'Single Pay'
          ? 'Use your assets to cover the policy cost with a one-time payment.'
          : `This is a ${paymentOption} policy. You can fund it with a lump sum or monthly contributions ${paymentDescription}.`}
      </p>
    </div>
  );
}

interface PolicyCostSummaryProps {
  paymentOption: LtcFinancialPlanningPaymentOption;
  paymentDescription: string;
  policyPremium: number;
  totalAllocation: number;
  remainingToFund: number;
  fundingProgress: number;
  isFunded: boolean;
}

function PolicyCostSummary({
  paymentOption,
  paymentDescription,
  policyPremium,
  totalAllocation,
  remainingToFund,
  fundingProgress,
  isFunded,
}: PolicyCostSummaryProps) {
  return (
    <div className="mx-auto mb-8 mt-8 max-w-3xl rounded-xl bg-gray-50 p-6 text-center">
      <div className="grid grid-cols-3 gap-4">
        <div>
          <p className="text-xs text-gray-700">
            Total Policy Cost
            {paymentOption !== 'Single Pay' && (
              <span className="block text-[10px]">({paymentDescription})</span>
            )}
          </p>
          <p className="text-lg font-bold text-rose-500">{formatCurrency(policyPremium)}</p>
        </div>
        <div>
          <p className="text-xs text-gray-700">Total Allocated</p>
          <p className="text-primary text-lg font-bold">{formatCurrency(totalAllocation)}</p>
        </div>
        <div>
          <p className="text-xs text-gray-700">Remaining</p>
          <p className="text-lg font-bold text-green-500">{formatCurrency(remainingToFund)}</p>
        </div>
      </div>

      <div className="mt-4">
        <div className="relative h-3 w-full overflow-hidden rounded-full bg-gray-200">
          <div
            className="bg-primary absolute inset-y-0 left-0 transition-all duration-300"
            style={{ width: `${fundingProgress}%` }}
          />
        </div>
        <p className="mt-2 text-sm text-gray-600">
          {isFunded
            ? '✓ Policy is fully funded!'
            : `${formatCurrency(remainingToFund)} more needed to fund the policy`}
        </p>
      </div>
    </div>
  );
}

function EmptyFundableAssetsMessage() {
  return (
    <div className="rounded-xl border border-gray-200 bg-white p-8 text-center">
      <p className="text-gray-600">
        All your assets are set as priority (protected). Please go back and adjust your priorities
        to use some assets for funding.
      </p>
    </div>
  );
}

interface ProtectedAssetsNoticeProps {
  count: number;
}

function ProtectedAssetsNotice({ count }: ProtectedAssetsNoticeProps) {
  return (
    <div className="bg-primary/5 mx-auto mt-6 max-w-3xl rounded-xl p-4">
      <p className="text-sm text-gray-700">
        <span className="text-primary font-semibold">{count} asset(s)</span> are protected and
        won&apos;t be used for policy funding. You&apos;ll keep these assets intact.
      </p>
    </div>
  );
}
